'use client';

import { ApolloLink, HttpLink, split } from '@apollo/client/core';
import { getMainDefinition } from '@apollo/client/utilities';
import {
  ApolloNextAppProvider,
  ApolloClient,
  InMemoryCache,
  SSRMultipartLink,
} from '@apollo/client-integration-nextjs';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { getSession, signOut } from 'next-auth/react';

const API_URL = process.env.NEXT_PUBLIC_API_URL || '';
const SOCKET_URL = process.env.NEXT_PUBLIC_SOCKET_URL || '';

const APP_API_URL = process.env.NEXT_PUBLIC_APP_API_URL || '';
const APP_API_URL_JWT = process.env.NEXT_PUBLIC_APP_API_URL_JWT || '';

const HPA_API_URL = process.env.NEXT_PUBLIC_HPA_API_URL || '';
const HPA_API_URL_JWT = process.env.NEXT_PUBLIC_HPA_API_URL_JWT || '';

const DASHBOARD_API_URL = process.env.NEXT_PUBLIC_DASHBOARD_API_URL || '';
const DASHBOARD_API_URL_JWT = process.env.NEXT_PUBLIC_DASHBOARD_API_URL_JWT || '';

const ERP_API_URL = process.env.NEXT_PUBLIC_ERP_API_URL || '';
const ERP_API_URL_JWT = process.env.NEXT_PUBLIC_ERP_API_URL_JWT || '';

/**
 * Memory에서 accessToken 가져오기
 * AuthContext에서 설정한 window.__accessToken 사용
 */
export function getAccessToken(): string | null {
  if (typeof window !== 'undefined') {
    return window.__accessToken || null;
  }
  return null;
}

type SessionPromise = ReturnType<typeof getSession>;
let sessionPromise: SessionPromise | null = null;

export async function getAccessTokenFromSession(): Promise<string | null> {
  if (typeof window === 'undefined') {
    return null;
  }

  const cachedToken = getAccessToken();
  if (cachedToken) {
    return cachedToken;
  }

  if (!sessionPromise) {
    sessionPromise = getSession().finally(() => {
      sessionPromise = null;
    });
  }

  const session = await sessionPromise;
  const token =
    (session as { serverAccessToken?: string | null; accessToken?: string | null })
      ?.serverAccessToken ||
    (session as { accessToken?: string | null })?.accessToken ||
    null;

  if (token) {
    window.__accessToken = token;
  }

  return token;
}

/**
 * 인증 에러 처리
 */
export function handleAuthError(hadAuthToken: boolean) {
  if (typeof window !== 'undefined') {
    const hasAccessToken = Boolean(window.__accessToken);
    const hasRefreshToken = Boolean(window.__refreshToken);
    if (!hadAuthToken || (!hasAccessToken && !hasRefreshToken)) {
      return;
    }
    // Memory에서 토큰 제거
    window.__accessToken = undefined;
    window.__refreshToken = undefined;
    // 로그아웃
    signOut({ callbackUrl: '/login' });
  }
}

export function makeClient() {
  const httpLink = new HttpLink({
    uri: API_URL,
    credentials: 'include',
  });

  const httpLinkApp = new HttpLink({
    uri: APP_API_URL,
  });

  const httpLinkHpa = new HttpLink({
    uri: HPA_API_URL,
  });

  const httpLinkDashboard = new HttpLink({
    uri: DASHBOARD_API_URL,
  });

  const httpLinkErp = new HttpLink({
    uri: ERP_API_URL,
  });

  // Error Link - 401/UNAUTHENTICATED 에러 처리
  // Apollo Client 4.x uses a combined 'error' property
  const errorLink = onError(({ error, operation }) => {
    const context = operation.getContext();
    // 선택적인 HPA 실행 설정 조회 실패는 화면에서 처리하고 MDA 로그인은 유지한다.
    // 인증 헤더와 서버 검증, 다른 요청의 인증 오류 처리는 그대로 적용한다.
    if (
      context.clientName === 'httpLinkHpa' &&
      context.handleAuthErrorLocally === true &&
      operation.operationName === 'CrystalRevealExecutionConfigs'
    ) {
      return;
    }
    const hadAuthToken = Boolean(context.hasAuthToken);
    // Check for GraphQL errors (using error as CombinedGraphQLErrors)
    if (error && 'errors' in error && Array.isArray(error.errors)) {
      for (const err of error.errors) {
        // UNAUTHENTICATED 또는 Unauthorized 에러
        if (
          err.extensions?.code === 'UNAUTHENTICATED' ||
          err.message?.includes('Unauthorized') ||
          err.message?.includes('401') ||
          err.message?.includes('Insufficient permissions')
        ) {
          console.error('Authentication error:', err.message);
          handleAuthError(hadAuthToken);
          return;
        }
      }
    }

    // Check for network errors
    if (error && 'statusCode' in error) {
      const statusCode = (error as { statusCode?: number }).statusCode;
      if (statusCode === 401) {
        console.error('Network authentication error');
        handleAuthError(hadAuthToken);
      }
    }
  });

  // Auth Link - Keycloak 토큰은 box-api 전용
  const authLink = setContext(async (_, { headers }) => {
    const token = await getAccessTokenFromSession();
    return {
      hasAuthToken: Boolean(token),
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const authLinkApp = setContext((_, { headers }) => {
    const token = APP_API_URL_JWT;
    return {
      hasAuthToken: Boolean(token),
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const authLinkHpa = setContext((_, { headers }) => {
    const token = HPA_API_URL_JWT;
    return {
      hasAuthToken: Boolean(token),
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const authLinkDashboard = setContext((_, { headers }) => {
    const token = DASHBOARD_API_URL_JWT;
    return {
      hasAuthToken: Boolean(token),
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const authLinkErp = setContext((_, { headers }) => {
    const token = ERP_API_URL_JWT;
    return {
      hasAuthToken: Boolean(token),
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const combineLink = () =>
    ApolloLink.split(
      (operation) => operation.getContext().clientName === 'httpLinkHpa',
      authLinkHpa.concat(httpLinkHpa),
      ApolloLink.split(
        (operation) => operation.getContext().clientName === 'httpLinkApp',
        authLinkApp.concat(httpLinkApp),
        ApolloLink.split(
          (operation) => operation.getContext().clientName === 'httpLinkDashboard',
          authLinkDashboard.concat(httpLinkDashboard),
          ApolloLink.split(
            (operation) => operation.getContext().clientName === 'httpLinkErp',
            authLinkErp.concat(httpLinkErp),
            authLink.concat(httpLink),
          ),
        ),
      ),
    );

  const wsLink = new GraphQLWsLink(
    createClient({
      url: SOCKET_URL,
      connectionParams: async () => {
        const token = await getAccessTokenFromSession();

        if (!token) {
          return {};
        }

        const bearerToken = `Bearer ${token}`;
        return {
          authorization: bearerToken,
          Authorization: bearerToken,
          accessToken: token,
          token,
        };
      },
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
    },
    wsLink,
    ApolloLink.split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return definition.kind === 'OperationDefinition' && definition.operation === 'mutation';
      },
      combineLink(),
      combineLink(),
    ),
  );

  // errorLink를 체인 앞에 추가
  const linkWithError = ApolloLink.from([errorLink, splitLink]);

  return new ApolloClient({
    cache: new InMemoryCache(),
    link:
      typeof window === 'undefined'
        ? ApolloLink.from([
            new SSRMultipartLink({
              stripDefer: true,
            }),
            linkWithError,
          ])
        : linkWithError,
  });
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  return <ApolloNextAppProvider makeClient={makeClient}>{children}</ApolloNextAppProvider>;
}
